import Tesseract from 'tesseract.js';
import { PDFDocument } from 'pdf-lib';

/**
 * Invoice Data Extraction - Extract structured data from invoices
 */

// Extract invoice data using pattern matching and OCR
export const extractInvoiceData = async (file, onProgress) => {
  onProgress(0);
  
  try {
    // First, try to extract text from PDF
    let extractedText = '';
    
    // For PDF files, we'll use OCR if needed
    if (file.type === 'application/pdf') {
      // In a real implementation, you'd extract text directly from PDF
      // For now, we'll use OCR as a fallback
      extractedText = await extractTextFromPDF(file, onProgress);
    } else if (file.type.startsWith('image/')) {
      // For images, use OCR directly
      extractedText = await extractTextWithOCR(file, onProgress);
    } else {
      throw new Error('Unsupported file type for invoice extraction');
    }
    
    onProgress(50);
    
    // Parse invoice data from extracted text
    const invoiceData = parseInvoiceText(extractedText);
    
    onProgress(90);
    
    // Enhance with additional detection
    const enhancedData = enhanceInvoiceData(invoiceData, extractedText);
    
    onProgress(100);
    
    return enhancedData;
    
  } catch (error) {
    throw new Error(`Invoice extraction failed: ${error.message}`);
  }
};

// Extract text from PDF (simplified - would need pdf.js in production)
const extractTextFromPDF = async (file, onProgress) => {
  // Placeholder - in production, use pdf.js to extract text
  // For now, return empty string and rely on OCR
  onProgress(30);
  return '';
};

// Extract text using OCR
const extractTextWithOCR = async (file, onProgress) => {
  const worker = await Tesseract.createWorker('eng', 1, {
    logger: m => {
      if (m.status === 'recognizing text') {
        const progress = Math.round((m.progress * 100));
        onProgress(10 + (progress * 0.4));
      }
    }
  });
  
  const { data: { text } } = await worker.recognize(file);
  await worker.terminate();
  
  return text;
};

// Parse invoice text to extract structured data
const parseInvoiceText = (text) => {
  const invoiceData = {
    invoiceNumber: null,
    date: null,
    dueDate: null,
    vendor: {
      name: null,
      address: null,
      phone: null,
      email: null,
      taxId: null,
    },
    customer: {
      name: null,
      address: null,
      phone: null,
      email: null,
    },
    items: [],
    subtotal: null,
    tax: null,
    discount: null,
    total: null,
    currency: 'USD',
    paymentTerms: null,
    notes: null,
  };
  
  const lines = text.split('\n').map(line => line.trim()).filter(line => line);
  
  // Extract invoice number
  const invoiceNumberMatch = text.match(/invoice\s*(?:#|number|no\.?)[\s:]*([A-Z0-9\-]+)/i);
  if (invoiceNumberMatch) {
    invoiceData.invoiceNumber = invoiceNumberMatch[1];
  }
  
  // Extract dates
  const datePatterns = [
    /(?:invoice\s*)?date[\s:]*(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})/i,
    /date[\s:]*(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})/i,
  ];
  
  datePatterns.forEach(pattern => {
    const match = text.match(pattern);
    if (match && !invoiceData.date) {
      invoiceData.date = match[1];
    }
  });
  
  const dueDateMatch = text.match(/due\s*date[\s:]*(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})/i);
  if (dueDateMatch) {
    invoiceData.dueDate = dueDateMatch[1];
  }
  
  // Extract totals
  const totalMatch = text.match(/total[\s:]*\$?([\d,]+\.?\d*)/i);
  if (totalMatch) {
    invoiceData.total = parseFloat(totalMatch[1].replace(/,/g, ''));
  }
  
  const subtotalMatch = text.match(/subtotal[\s:]*\$?([\d,]+\.?\d*)/i);
  if (subtotalMatch) {
    invoiceData.subtotal = parseFloat(subtotalMatch[1].replace(/,/g, ''));
  }
  
  const taxMatch = text.match(/tax[\s:]*\$?([\d,]+\.?\d*)/i);
  if (taxMatch) {
    invoiceData.tax = parseFloat(taxMatch[1].replace(/,/g, ''));
  }
  
  // Extract vendor information
  const vendorNamePatterns = [
    /(?:from|vendor|supplier|company)[\s:]*([A-Z][A-Za-z\s&]+)/i,
  ];
  
  vendorNamePatterns.forEach(pattern => {
    const match = text.match(pattern);
    if (match && !invoiceData.vendor.name) {
      invoiceData.vendor.name = match[1].trim();
    }
  });
  
  // Extract email
  const emailMatch = text.match(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/);
  if (emailMatch) {
    invoiceData.vendor.email = emailMatch[1];
  }
  
  // Extract phone
  const phoneMatch = text.match(/(\+?1?[\s\-]?\(?[0-9]{3}\)?[\s\-]?[0-9]{3}[\s\-]?[0-9]{4})/);
  if (phoneMatch) {
    invoiceData.vendor.phone = phoneMatch[1];
  }
  
  // Extract line items (simplified - would need more sophisticated parsing)
  const itemPattern = /(\d+)\s+(.+?)\s+\$?([\d,]+\.?\d*)/g;
  let itemMatch;
  while ((itemMatch = itemPattern.exec(text)) !== null) {
    invoiceData.items.push({
      quantity: parseInt(itemMatch[1]),
      description: itemMatch[2].trim(),
      unitPrice: parseFloat(itemMatch[3].replace(/,/g, '')),
      total: parseInt(itemMatch[1]) * parseFloat(itemMatch[3].replace(/,/g, '')),
    });
  }
  
  return invoiceData;
};

// Enhance invoice data with additional detection
const enhanceInvoiceData = (invoiceData, text) => {
  // Detect currency
  const currencyMatch = text.match(/\$|USD|EUR|GBP|JPY|CAD|AUD/i);
  if (currencyMatch) {
    const currency = currencyMatch[0].toUpperCase();
    if (currency === '$') {
      invoiceData.currency = 'USD';
    } else {
      invoiceData.currency = currency;
    }
  }
  
  // Detect payment terms
  const paymentTermsMatch = text.match(/(?:payment\s*terms?|terms?)[\s:]*([^\n]+)/i);
  if (paymentTermsMatch) {
    invoiceData.paymentTerms = paymentTermsMatch[1].trim();
  }
  
  // Calculate missing totals if possible
  if (!invoiceData.total && invoiceData.items.length > 0) {
    const itemsTotal = invoiceData.items.reduce((sum, item) => sum + (item.total || 0), 0);
    invoiceData.subtotal = invoiceData.subtotal || itemsTotal;
    invoiceData.total = invoiceData.subtotal + (invoiceData.tax || 0) - (invoiceData.discount || 0);
  }
  
  return invoiceData;
};

// Export invoice data as JSON
export const exportInvoiceAsJSON = async (file, onProgress) => {
  const invoiceData = await extractInvoiceData(file, (progress) => {
    onProgress(progress * 0.9);
  });
  
  onProgress(90);
  
  const jsonString = JSON.stringify(invoiceData, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  
  onProgress(100);
  
  return {
    blob,
    filename: file.name.replace(/\.(pdf|jpg|jpeg|png)$/i, '_invoice_data.json'),
    data: invoiceData,
  };
};

// Export invoice data as CSV
export const exportInvoiceAsCSV = async (file, onProgress) => {
  const invoiceData = await extractInvoiceData(file, (progress) => {
    onProgress(progress * 0.9);
  });
  
  onProgress(90);
  
  let csv = 'Field,Value\n';
  csv += `Invoice Number,${invoiceData.invoiceNumber || ''}\n`;
  csv += `Date,${invoiceData.date || ''}\n`;
  csv += `Due Date,${invoiceData.dueDate || ''}\n`;
  csv += `Vendor Name,${invoiceData.vendor.name || ''}\n`;
  csv += `Vendor Email,${invoiceData.vendor.email || ''}\n`;
  csv += `Vendor Phone,${invoiceData.vendor.phone || ''}\n`;
  csv += `Subtotal,${invoiceData.subtotal || ''}\n`;
  csv += `Tax,${invoiceData.tax || ''}\n`;
  csv += `Total,${invoiceData.total || ''}\n`;
  csv += `Currency,${invoiceData.currency || ''}\n`;
  
  if (invoiceData.items.length > 0) {
    csv += '\nItems\n';
    csv += 'Quantity,Description,Unit Price,Total\n';
    invoiceData.items.forEach(item => {
      csv += `${item.quantity},${item.description},${item.unitPrice},${item.total}\n`;
    });
  }
  
  const blob = new Blob([csv], { type: 'text/csv' });
  
  onProgress(100);
  
  return {
    blob,
    filename: file.name.replace(/\.(pdf|jpg|jpeg|png)$/i, '_invoice_data.csv'),
    data: invoiceData,
  };
};

// Extract invoice data from multiple files (bulk processing)
export const extractInvoiceDataBulk = async (files, onProgress) => {
  const results = [];
  const totalFiles = files.length;
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const fileProgress = (i / totalFiles) * 100;
    
    try {
      const invoiceData = await extractInvoiceData(file, (progress) => {
        onProgress(fileProgress + (progress * (1 / totalFiles)));
      });
      
      results.push({
        filename: file.name,
        success: true,
        data: invoiceData,
      });
    } catch (error) {
      results.push({
        filename: file.name,
        success: false,
        error: error.message,
      });
    }
  }
  
  onProgress(100);
  return results;
};


